/**
 * Shared numbered-pagination model.
 * Router-free / API-free / selector-free helpers for compact page windows.
 *
 * Invariant: every normalized total page, current page, and generated page
 * target is a finite safe integer with
 * 1 <= currentPage <= totalPages <= Number.MAX_SAFE_INTEGER.
 */

/**
 * Safe total-page count from a result count and page size.
 * Preserves ceil(count / pageSize) semantics while guaranteeing a safe integer.
 * Handles zero, negative, NaN, invalid page size, fractional inputs, and overflow.
 */
export function totalPagesFromCount(count, pageSize) {
  const safeCount = Number(count);
  const safeSize = Number(pageSize);
  if (!Number.isFinite(safeCount) || safeCount <= 0) return 1;
  if (!Number.isFinite(safeSize) || safeSize <= 0) return 1;

  const raw = Math.ceil(safeCount / safeSize);
  if (!Number.isFinite(raw) || raw < 1) return 1;
  if (raw > Number.MAX_SAFE_INTEGER) return Number.MAX_SAFE_INTEGER;
  return raw;
}

/**
 * Normalize total pages to a finite safe integer of at least 1.
 *
 * Policy:
 * 1. Convert with Number(...)
 * 2. Non-finite, zero, or negative → 1
 * 3. Positive fractional values truncate with Math.floor
 * 4. Values above Number.MAX_SAFE_INTEGER clamp to Number.MAX_SAFE_INTEGER
 */
export function normalizeTotalPages(totalPages) {
  const numeric = Number(totalPages);
  if (!Number.isFinite(numeric) || numeric <= 0) return 1;

  const floored = Math.floor(numeric);
  if (floored < 1) return 1;
  if (floored > Number.MAX_SAFE_INTEGER) return Number.MAX_SAFE_INTEGER;
  return floored;
}

/**
 * Normalize current page to a finite safe integer within [1, totalPages].
 *
 * Policy:
 * 1. Normalize total first
 * 2. Convert current with Number(...)
 * 3. Non-finite or non-positive → 1
 * 4. Positive fractional values truncate with Math.floor
 * 5. Clamp to [1, normalizedTotal]
 */
export function normalizeCurrentPage(currentPage, totalPages) {
  const total = normalizeTotalPages(totalPages);
  const numeric = Number(currentPage);
  if (!Number.isFinite(numeric) || numeric < 1) return 1;

  let current = Math.floor(numeric);
  if (current < 1) current = 1;
  if (current > total) current = total;
  return current;
}

/**
 * Compact numbered pagination window.
 * Returns items: { type: "page", page } | { type: "ellipsis", key }.
 *
 * Behavior (accepted Deals window):
 * - 7 or fewer pages → every page
 * - larger totals → first, last, current, current ±1,
 *   plus bounded start/end neighbors; ellipsis for every gap
 *
 * Every page value is a safe integer in [1, normalizedTotal].
 */
export function buildCompactPaginationItems(currentPage, totalPages) {
  const total = normalizeTotalPages(totalPages);
  const current = normalizeCurrentPage(currentPage, total);

  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => ({
      type: "page",
      page: index + 1,
    }));
  }

  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) {
    pages.add(2);
    pages.add(3);
  }
  if (current >= total - 2) {
    pages.add(total - 1);
    pages.add(total - 2);
  }

  const sorted = [...pages]
    .filter(
      (page) =>
        Number.isSafeInteger(page) && page >= 1 && page <= total,
    )
    .sort((a, b) => a - b);

  const items = [];
  let previous = null;
  for (const page of sorted) {
    if (previous != null && page - previous > 1) {
      items.push({ type: "ellipsis", key: `e-${previous}-${page}` });
    }
    items.push({ type: "page", page });
    previous = page;
  }
  return items;
}
